import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, CheckCircle2, Database, BrainCircuit, FileSearch, PlayCircle, FileCode } from 'lucide-react';

// New component for the animated title
const AnimatedTitle = ({ text }) => {
  return (
    <span className="text-sm font-medium flex overflow-hidden">
      {text.split('').map((char, index) => (
        <span
          key={index}
          className="wave-animation"
          style={{ 
            animationDelay: `${index * 0.05}s` // Changed from 0.08s to 0.05s
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );
};

const QueryProcessingTerminal = ({ 
  userQuery = "", 
  availableTables = [],
  isLoading = true,
  totalDuration = 120000 // Default 120 seconds max
}) => {
  const [messages, setMessages] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [stage, setStage] = useState('init');
  const messagesEndRef = useRef(null);
  const timerRef = useRef(null);
  const stagesProcessed = useRef(new Set()).current;

  // Get 1-2 random tables from available tables that might be relevant
  const getRelevantTables = () => {
    if (!availableTables || availableTables.length === 0) return ["data"];
    
    const shuffled = [...availableTables].sort(() => 0.5 - Math.random());
    const count = Math.min(Math.max(1, Math.floor(Math.random() * 2) + 1), shuffled.length);
    return shuffled.slice(0, count);
  };

  const relevantTables = useRef(getRelevantTables()).current;

  // Create more natural-sounding message variations
  const getRandomMessage = (type) => {
    const messages = {
      processing: [
        "Analyzing your query...",
        "Processing your request...",
        "Interpreting your question..."
      ],
      schema: [
        `Loading data for ${relevantTables.join(", ")}...`,
        `Accessing relevant tables: ${relevantTables.join(", ")}...`
      ],
      generating: [
        "Generating code to answer your question...",
        "Translating your question into code..."
      ],
      review: [
        "Reviewing generated code for accuracy...",
        "Double-checking result relevance..."
      ],
      fixing: [
        "Fixing potential issues in generated code...",
        "Addressing edge cases in the logic..."
      ],
      execution: [
        "Executing your query...",
        "Running generated code..."
      ],
      completion: [
        "Query completed successfully!",
        "Results ready to display!"
      ]
    };
    
    const options = messages[type] || messages.processing;
    return options[Math.floor(Math.random() * options.length)];
  };

  // Add a message with the appropriate icon
  const addMessage = (type, text) => {
    const icons = {
      init: <PlayCircle className="w-3.5 h-3.5 text-primary" />,
      schema: <Database className="w-3.5 h-3.5 text-primary" />,
      processing: <BrainCircuit className="w-3.5 h-3.5 text-secondary" />,
      generating: <FileCode className="w-3.5 h-3.5 text-secondary" />,
      review: <FileSearch className="w-3.5 h-3.5 text-accent" />,
      fixing: <FileCode className="w-3.5 h-3.5 text-accent" />,
      execution: <Sparkles className="w-3.5 h-3.5 text-secondary" />,
      completion: <CheckCircle2 className="w-3.5 h-3.5 text-accent" />
    };
    
    // Use a default icon if not specified
    const icon = icons[type] || icons.processing;
    
    // Mark this stage as processed
    stagesProcessed.add(type);
    
    setMessages(prev => {
      // Only keep last 3 messages to avoid clutter
      const updatedMessages = [...prev, {
        id: Date.now(),
        type,
        text,
        icon
      }];
      
      if (updatedMessages.length > 3) {
        return updatedMessages.slice(updatedMessages.length - 3);
      }
      return updatedMessages;
    });
    
    // Update the current stage
    setStage(type);
  };

// This effect was causing the page scroll issue on the dashboard
// Since we have a fixed height container (100px) with only 3 messages max,
// scrolling is not needed and was causing unwanted page scrolling
/*
useEffect(() => {
  if (messagesEndRef.current) {
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }
}, [messages]);
*/

  // Reset effect - clear everything when loading starts or stops
  useEffect(() => {
    if (isLoading) {
      if (!startTime) {
        setMessages([]);
        setStartTime(Date.now());
        setStage('init');
        stagesProcessed.clear();
        
        // Add initial message
        const initialQuery = userQuery.length > 40 
          ? userQuery.substring(0, 40) + "..." 
          : userQuery;
        
        if (initialQuery.trim()) {
          addMessage('init', `Processing: "${initialQuery}"`);
        } else {
          addMessage('init', 'Processing your query...');
        }
      }
    } else {
      // Clean up when loading stops
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setStartTime(null);
      setElapsedTime(0);
    }
  }, [isLoading, userQuery]);

  // Progression effect - handle the animation stages
  useEffect(() => {
    if (!isLoading || !startTime) return;
    
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setElapsedTime(elapsed);
      
      // Adjusted timing and made sure each stage only runs once
      const stages = [
        { time: 1500, type: 'schema' },
        { time: 3000, type: 'processing' },
        { time: 5000, type: 'generating' },
        { time: 7000, type: 'review' },
        { time: 9000, type: 'fixing' },     // Reduced time from 12000 to 9000
        { time: 11000, type: 'execution' }  // Reduced time from 20000 to 11000
      ];
      
      // Find the current stage based on elapsed time
      for (const stageConfig of stages) {
        if (elapsed > stageConfig.time && !stagesProcessed.has(stageConfig.type)) {
          addMessage(stageConfig.type, getRandomMessage(stageConfig.type));
          break; // Only process one stage at a time
        }
      }
      
      // If we're reaching the end of our timing, make sure we get to execution
      if (elapsed > 12000 && !stagesProcessed.has('execution')) {
        addMessage('execution', getRandomMessage('execution'));
      }
      
    }, 500);
    
    timerRef.current = timer;
    return () => {
      clearInterval(timer);
      timerRef.current = null;
    };
  }, [isLoading, startTime, stage]);
  
  // Keep track if dots for the typing animation
  const [dots, setDots] = useState("");
  
  // Dot animation effect
  useEffect(() => {
    if (!isLoading) return;
    
    const dotTimer = setInterval(() => {
      setDots(prev => {
        if (prev.length >= 3) return "";
        return prev + ".";
      });
    }, 500);
    
    return () => clearInterval(dotTimer);
  }, [isLoading]);

  // Custom scrollbar hiding style
  const hideScrollbarStyle = {
    msOverflowStyle: 'none',  /* IE and Edge */
    scrollbarWidth: 'none',   /* Firefox */
  };

  return (
    <div className="mt-4 ml-0 mr-auto w-full max-w-md">
      <div className="py-2 overflow-hidden" style={{ height: '100px' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <AnimatedTitle text="Processing your query" />
            <span className="text-xs text-text-light ml-2">
              {elapsedTime > 0 && `${(elapsedTime / 1000).toFixed(1)}s`}
            </span>
          </div>
        </div>
        
        {/* Fixed height container for messages */}
        <div className="h-16" style={{ minHeight: '64px' }}>
          {/* Messages with hidden scrollbar */}
          <div 
            className="space-y-2 h-full overflow-y-auto" 
            style={hideScrollbarStyle}
          >
            {messages.map((message) => (
              <div key={message.id} className="flex items-start">
                <div className="mr-2 mt-0.5 flex-shrink-0">
                  {message.icon}
                </div>
                <div className="flex-1">
                  <span className="text-xs text-text-light">
                    {message.text}
                  </span>
                </div>
              </div>
            ))}
            
            {/* Render dummy hidden messages to ensure the height is consistent */}
            {messages.length === 0 && (
              <div className="invisible">
                <div className="h-4 mb-2"></div>
                <div className="h-4 mb-2"></div>
                <div className="h-4"></div>
              </div>
            )}
            
            {/* Reference to allow scrolling to bottom */}
            <div ref={messagesEndRef} />
          </div>
        </div>
        
        {/* Hide scrollbar for Webkit browsers and add wave animation */}
        <style>
          {`
            .overflow-y-auto::-webkit-scrollbar {
              display: none;
            }
            
            @keyframes textWave {
              0% {
                color: #6B7280; /* Light gray */
                opacity: 0.85;
              }
              50% {
                color: #111827; /* Very dark gray/almost black */
                opacity: 1;
              }
              100% {
                color: #6B7280; /* Back to light gray */
                opacity: 0.85;
              }
            }
            
            .wave-animation {
              animation: textWave 1.2s ease-in-out infinite; /* Changed from 2s to 1.2s */
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default QueryProcessingTerminal;